// src/components/generic/ImageCaptureUpload/ImageCaptureUpload.tsx
//
// Orchestrator: CameraCapture → ImageCropEditor → Upload → onComplete
//
// Three phases:
//   1. CAPTURE  — CameraCapture overlay (take photo or pick from gallery)
//   2. CROP     — ImageCropEditor (crop, rotate, skip)
//   3. UPLOAD   — Upload blob via UploadedFileService, show progress
//
// Usage:
//   <ImageCaptureUpload
//     opened={isCameraOpen}
//     onClose={() => setIsCameraOpen(false)}
//     onComplete={(file) => handleAttachFile(file)}
//     filePath="parking-sessions"
//     cropAspectRatio="free"
//   />

import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Portal,
  Stack,
  Text,
  Button,
  Transition,
  RingProgress,
  ThemeIcon,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { UploadedFile, UploadedFileService } from "@/utils/api";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { CameraCapture } from "@/components/generic/CameraCapture";
import { ImageCropEditor } from "@/components/generic/ImageCropEditor";
import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface ImageCaptureUploadProps {
  /** Whether the component is open */
  opened: boolean;

  /** Called when the user closes / cancels at any phase */
  onClose: () => void;

  /** Called with the uploaded file record on success */
  onComplete: (file: UploadedFile) => void;

  /**
   * Storage folder path hint passed to UploadedFileService.create.
   * E.g. "parking-sessions", "glovebox", "logbook"
   */
  filePath?: string;

  /**
   * Aspect ratio for the crop editor.
   * Passed through to ImageCropEditor.
   * Default: "free"
   */
  cropAspectRatio?: "free" | "square" | "4:3" | "3:4" | "16:9" | number;

  /** Whether to show the aspect ratio selector in crop phase. Default: true */
  showAspectRatioSelector?: boolean;

  /** Whether to show rotation controls in crop phase. Default: true */
  showCropRotation?: boolean;

  /**
   * Skip the crop step entirely — go straight from capture to upload.
   * Default: false
   */
  skipCrop?: boolean;

  /**
   * Whether to show "Skip" button in the crop editor
   * (uploads the uncropped image). Default: true
   */
  allowSkipCrop?: boolean;

  /** Max dimension for captured images. Default: 1920 */
  maxDimension?: number;

  /** JPEG quality for output (0–1). Default: 0.85 */
  quality?: number;

  /** Camera title text */
  cameraTitle?: string;

  /** Camera hint text (shown in framing guide) */
  cameraHint?: string;

  /** Crop editor title text */
  cropTitle?: string;

  /** Custom filename for the uploaded file (without extension) */
  fileName?: string;

  /** Optional file name/label for the uploaded file record */
  fileLabel?: string;

  /** Optional notes attached to the uploaded file */
  fileNotes?: string;
}

type Phase = "capture" | "crop" | "uploading" | "success" | "error";

// ============================================
// Constants
// ============================================

/** Auto-dismiss delay after successful upload (ms) */
const SUCCESS_DISMISS_MS = 800;

/** Simulated upload progress step interval (ms) */
const PROGRESS_TICK_MS = 150;

// ============================================
// Component
// ============================================

const ImageCaptureUploadComponent: React.FC<ImageCaptureUploadProps> = ({
  opened,
  onClose,
  onComplete,
  filePath,
  cropAspectRatio = "free",
  showAspectRatioSelector = true,
  showCropRotation = true,
  skipCrop = false,
  allowSkipCrop = true,
  maxDimension = 1920,
  quality = 0.85,
  cameraTitle,
  cameraHint,
  cropTitle,
  fileName,
  fileLabel,
  fileNotes,
}) => {
  const { t } = useTranslation(["common"]);

  // ── Phase state ──

  const [phase, setPhase] = useState<Phase>("capture");
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [_uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);

  // Refs for cleanup
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCancelledRef = useRef(false);

  // ── Reset when opened changes ──

  useEffect(() => {
    if (opened) {
      setPhase("capture");
      setCapturedBlob(null);
      setUploadProgress(0);
      setErrorMessage(null);
      setUploadedFile(null);
      isCancelledRef.current = false;
    } else {
      // Cleanup timers on close
      isCancelledRef.current = true;
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    }
  }, [opened]);

  // ── Cleanup on unmount ──

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  // ============================================
  // Phase 1: Camera capture complete
  // ============================================

  const handleCapture = useCallback(
    (blob: Blob) => {
      setCapturedBlob(blob);

      if (skipCrop) {
        // Go straight to upload
        setPhase("uploading");
      } else {
        setPhase("crop");
      }
    },
    [skipCrop]
  );

  const handleCameraClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // ============================================
  // Phase 2: Crop complete
  // ============================================

  const handleCropConfirm = useCallback((croppedBlob: Blob) => {
    setCapturedBlob(croppedBlob);
    setPhase("uploading");
  }, []);

  const handleCropSkip = useCallback((originalBlob: Blob) => {
    setCapturedBlob(originalBlob);
    setPhase("uploading");
  }, []);

  const handleCropClose = useCallback(() => {
    // Go back to capture phase
    setCapturedBlob(null);
    setPhase("capture");
  }, []);

  // ============================================
  // Phase 3: Upload
  // ============================================

  const performUpload = useCallback(async () => {
    if (!capturedBlob) return;

    isCancelledRef.current = false;
    setErrorMessage(null);
    setUploadProgress(0);

    // Simulate progress since we don't have real upload progress events
    // The progress moves to ~80% during upload, then jumps to 100% on complete
    let progress = 0;
    progressTimerRef.current = setInterval(() => {
      progress = Math.min(progress + Math.random() * 8 + 2, 80);
      setUploadProgress(Math.round(progress));
    }, PROGRESS_TICK_MS);

    try {
      const timestamp = Date.now();
      const uploadFileName = fileName
        ? `${fileName}.jpg`
        : `photo_${timestamp}.jpg`;

      const file = new File([capturedBlob], uploadFileName, {
        type: capturedBlob.type || "image/jpeg",
      });

      const result = await UploadedFileService.create({
        files: [file],
        filePath: filePath || undefined,
        name: fileLabel || uploadFileName,
        notes: fileNotes || undefined,
        originalFilename: uploadFileName,
        mimeType: capturedBlob.type || "image/jpeg",
      });

      // Stop progress simulation
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }

      if (isCancelledRef.current) return;

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const uploadedFileData = Array.isArray(result.data)
          ? (result.data[0] as UploadedFile)
          : (result.data as UploadedFile);

        setUploadProgress(100);
        setUploadedFile(uploadedFileData);
        setPhase("success");

        // Auto-dismiss and notify parent
        successTimerRef.current = setTimeout(() => {
          if (!isCancelledRef.current) {
            onComplete(uploadedFileData);
          }
        }, SUCCESS_DISMISS_MS);
      } else {
        const errMsg =
          result.errors?.[0]?.errors?.[0] ||
          t("common:imageUpload.errors.uploadFailed", {
            defaultValue: "Failed to upload image. Please try again.",
          });
        setErrorMessage(errMsg);
        setPhase("error");
      }
    } catch (error) {
      // Stop progress simulation
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }

      if (isCancelledRef.current) return;

      const errMsg =
        error instanceof Error
          ? error.message
          : t("common:imageUpload.errors.unexpected", {
            defaultValue: "An unexpected error occurred.",
          });
      setErrorMessage(errMsg);
      setPhase("error");
    }
  }, [capturedBlob, fileName, filePath, fileLabel, fileNotes, onComplete, t]);

  // Trigger upload when phase becomes "uploading"
  useEffect(() => {
    if (phase === "uploading" && capturedBlob) {
      performUpload();
    }
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Error recovery: retry or go back
  // ============================================

  const handleRetry = useCallback(() => {
    setErrorMessage(null);
    setPhase("uploading");
  }, []);

  const handleBackToCamera = useCallback(() => {
    setCapturedBlob(null);
    setErrorMessage(null);
    setUploadProgress(0);
    setPhase("capture");
  }, []);

  // ============================================
  // Render
  // ============================================

  if (!opened) return null;

  return (
    <>
      {/* ═══════ Phase 1: Camera ═══════ */}
      <CameraCapture
        opened={phase === "capture"}
        onClose={handleCameraClose}
        onCapture={handleCapture}
        title={
          cameraTitle ||
          t("common:imageUpload.cameraTitle", { defaultValue: "Take Photo" })
        }
        hint={cameraHint}
        maxDimension={maxDimension}
        quality={quality}
        showGuide={false}
        showGalleryButton={true}
      />

      {/* ═══════ Phase 2: Crop ═══════ */}
      <ImageCropEditor
        opened={phase === "crop"}
        onClose={handleCropClose}
        onConfirm={handleCropConfirm}
        onSkip={allowSkipCrop ? handleCropSkip : undefined}
        image={capturedBlob}
        aspectRatio={cropAspectRatio}
        showAspectRatioSelector={showAspectRatioSelector}
        showRotation={showCropRotation}
        showSkip={allowSkipCrop}
        maxOutputDimension={maxDimension}
        outputQuality={quality}
        title={
          cropTitle ||
          t("common:imageUpload.cropTitle", { defaultValue: "Crop Image" })
        }
      />

      {/* ═══════ Phase 3/4/5: Upload / Success / Error ═══════ */}
      {(phase === "uploading" || phase === "success" || phase === "error") && (
        <Portal>
          <Transition
            mounted={true}
            transition="fade"
            duration={200}
          >
            {(styles) => (
              <Box
                style={{
                  ...styles,
                  position: "fixed",
                  inset: 0,
                  zIndex: 1000,
                  backgroundColor: "rgba(0, 0, 0, 0.85)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  touchAction: "none",
                }}
              >
                <Stack
                  align="center"
                  gap="lg"
                  style={{
                    maxWidth: 320,
                    padding: 24,
                  }}
                >
                  {/* ── Uploading ── */}
                  {phase === "uploading" && (
                    <>
                      <RingProgress
                        size={100}
                        thickness={6}
                        roundCaps
                        sections={[
                          {
                            value: uploadProgress,
                            color: "brand",
                          },
                        ]}
                        label={
                          <Text
                            ta="center"
                            fw={700}
                            size="lg"
                            c="white"
                            style={{ fontVariantNumeric: "tabular-nums" }}
                          >
                            {uploadProgress}%
                          </Text>
                        }
                      />
                      <Text size="md" fw={500} c="white" ta="center">
                        {t("common:imageUpload.uploading", {
                          defaultValue: "Uploading...",
                        })}
                      </Text>
                      <Text size="sm" c="dimmed" ta="center">
                        {t("common:imageUpload.uploadingHint", {
                          defaultValue: "Please wait while your image is being uploaded.",
                        })}
                      </Text>
                    </>
                  )}

                  {/* ── Success ── */}
                  {phase === "success" && (
                    <>
                      <ThemeIcon
                        size={80}
                        radius="xl"
                        variant="light"
                        color="green"
                      >
                        <AppIcon iconName="Check" size={40} />
                      </ThemeIcon>
                      <Text size="md" fw={600} c="white" ta="center">
                        {t("common:imageUpload.success", {
                          defaultValue: "Image uploaded!",
                        })}
                      </Text>
                    </>
                  )}

                  {/* ── Error ── */}
                  {phase === "error" && (
                    <>
                      <ThemeIcon
                        size={80}
                        radius="xl"
                        variant="light"
                        color="red"
                      >
                        <AppIcon iconName="X" size={40} />
                      </ThemeIcon>
                      <Text size="md" fw={600} c="white" ta="center">
                        {t("common:imageUpload.errorTitle", {
                          defaultValue: "Upload Failed",
                        })}
                      </Text>
                      <Text size="sm" c="dimmed" ta="center">
                        {errorMessage}
                      </Text>

                      <Stack gap="xs" w="100%">
                        <Button
                          variant="filled"
                          size="lg"
                          fullWidth
                          onClick={handleRetry}
                          leftSection={<AppIcon iconName="Refresh" size={18} />}
                          styles={{ root: { minHeight: 48 } }}
                        >
                          {t("common:imageUpload.retry", {
                            defaultValue: "Retry",
                          })}
                        </Button>
                        <Button
                          variant="subtle"
                          color="gray"
                          size="md"
                          fullWidth
                          onClick={handleBackToCamera}
                          leftSection={<AppIcon iconName="Camera" size={18} />}
                          styles={{ root: { color: "white", minHeight: 44 } }}
                        >
                          {t("common:imageUpload.retake", {
                            defaultValue: "Take New Photo",
                          })}
                        </Button>
                        <Button
                          variant="subtle"
                          color="gray"
                          size="sm"
                          fullWidth
                          onClick={onClose}
                          styles={{ root: { color: "rgba(255,255,255,0.5)", minHeight: 44 } }}
                        >
                          {t("common:actions.cancel", {
                            defaultValue: "Cancel",
                          })}
                        </Button>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Box>
            )}
          </Transition>
        </Portal>
      )}
    </>
  );
};

export const ImageCaptureUpload = React.memo(ImageCaptureUploadComponent);